import {
  AdminApiError,
  type AdminClient,
  type ChainBet,
  type ChainMovement,
  type ChainTrace,
  type VerifyResponse,
} from '@apx/admin-sdk';
import { fmtDateTime, fmtMoney } from '../format.js';
import { betTypeLabel, formatResult } from './trace-format.js';
import { icon } from '../icons.js';

export interface TransactionTraceDeps {
  client: AdminClient;
  integratorId: string;
  roundEngineBaseUrl: string;
}

export function renderTransactionTracePage(root: HTMLElement, deps: TransactionTraceDeps): void {
  root.innerHTML = `
    <h1 class="page-title">Transaction Trace</h1>
    <p class="page-sub">
      Paste any transaction id (requestId, wallet tx id, integrator tx id, round id, or bet id)
      to see the full bet → debit → settle → win → jackpot → rollback chain across any game.
    </p>

    <div class="card">
      <h2>Search</h2>
      <div class="row">
        <label style="flex:1 1 320px">Transaction id
          <input id="trace-id" type="text" placeholder="requestId / wallet tx / integrator tx / round / bet id" />
        </label>
        <button id="trace-run" class="primary">Trace</button>
        <span id="trace-msg" class="muted" style="align-self:center"></span>
      </div>
    </div>

    <div id="trace-result"></div>
  `;

  const inputEl = root.querySelector<HTMLInputElement>('#trace-id')!;
  const runEl = root.querySelector<HTMLButtonElement>('#trace-run')!;
  const msgEl = root.querySelector<HTMLElement>('#trace-msg')!;
  const resultEl = root.querySelector<HTMLElement>('#trace-result')!;

  async function run(): Promise<void> {
    const id = inputEl.value.trim();
    if (!id) return;
    msgEl.className = 'muted';
    msgEl.textContent = 'Tracing…';
    runEl.disabled = true;
    try {
      const trace = await deps.client.getTrace(deps.integratorId, id);
      resultEl.innerHTML = renderTrace(trace, deps.client.traceCsvUrl(deps.integratorId, id));
      msgEl.textContent = '';
      // The trace carries the verdict; fetch the full provably-fair proof
      // (seeds, hash, re-derivation) and slot it into the placeholder.
      void loadVerifyProof(trace.round.roundId);
    } catch (err) {
      resultEl.innerHTML = '';
      msgEl.className = 'error';
      msgEl.textContent = describe(err);
    } finally {
      runEl.disabled = false;
    }
  }

  async function loadVerifyProof(roundId: string): Promise<void> {
    const slot = resultEl.querySelector<HTMLElement>('#trace-verify');
    if (!slot) return;
    slot.innerHTML = '<div class="card"><p class="muted">Loading verification proof…</p></div>';
    try {
      const v = await deps.client.verifyRound(deps.roundEngineBaseUrl, roundId);
      slot.innerHTML = renderVerifyProof(v);
    } catch (err) {
      // Non-fatal: round may be too new (no revealed seed yet) or non-greedyking
      // verify shape — show a soft note rather than breaking the trace.
      slot.innerHTML = `<div class="card"><p class="muted">Verification proof unavailable: ${escapeHtml(describe(err))}</p></div>`;
    }
  }

  runEl.addEventListener('click', () => void run());
  inputEl.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      void run();
    }
  });

  // Deep-link support: ?id=… prefills the input and runs immediately. Lets
  // other pages (e.g. player-detail's history rows) link straight into a trace.
  const prefill = new URLSearchParams(window.location.search).get('id');
  if (prefill) {
    inputEl.value = prefill;
    void run();
  }
}

function renderTrace(trace: ChainTrace, csvUrl: string): string {
  const r = trace.round;
  const result = escapeHtml(formatResult(r.gameId, r.resultData));
  return `
    <div class="card">
      <div class="row" style="align-items:flex-start">
        <h2 style="margin:0">Round</h2>
        <a href="${escapeHtml(csvUrl)}" download="apx-trace-${escapeHtml(r.roundId)}.csv"
           class="ghost" style="margin-left:auto;padding:6px 12px;text-decoration:none">Download CSV</a>
      </div>
      <div class="kv" style="margin-top:12px">
        <dt>Round</dt><dd><code title="${escapeHtml(r.roundId)}">${escapeHtml(r.roundId.slice(-12))}</code></dd>
        <dt>Game</dt><dd><code>${escapeHtml(r.gameId)}</code></dd>
        <dt>Result</dt><dd><code>${result}</code></dd>
        <dt>State</dt><dd><span class="pill ${stateClass(r.state)}">${escapeHtml(r.state)}</span></dd>
        <dt>Verifier verdict</dt><dd>${renderVerdict(r.verifyVerdict)}</dd>
      </div>
    </div>
    <div id="trace-verify"></div>
    ${trace.bets.length === 0 ? '<p class="muted">No bets on this trace.</p>' : renderBetsTable(trace.bets)}
  `;
}

/** Compact, scannable bet ledger: one row per bet (glanceable), expand a row
 *  to reveal its wallet movements + payout breakdown (detail on demand). */
function renderBetsTable(bets: ChainBet[]): string {
  return `
    <div class="card">
      <h2 style="margin:0 0 12px">Bets <span class="muted section-hint">(${bets.length})</span></h2>
      <div class="bet-ledger">
        <div class="bet-row bet-row--head">
          <span>Pick</span>
          <span>Player</span>
          <span class="num">Stake</span>
          <span>Outcome</span>
          <span>Accept</span>
          <span class="num">Payout</span>
          <span class="num">Moves</span>
          <span aria-hidden="true"></span>
        </div>
        ${bets.map(renderBetRow).join('')}
      </div>
    </div>
  `;
}

/** Provably-fair proof: how the round result is verified from the public seeds. */
function renderVerifyProof(v: VerifyResponse): string {
  const yn = (ok: boolean): string =>
    ok
      ? '<span style="color:var(--success);font-weight:600">✓ match</span>'
      : '<span style="color:var(--error);font-weight:600">✗ mismatch</span>';
  const stringifyResult = (r: unknown): string =>
    typeof r === 'object' && r !== null ? escapeHtml(JSON.stringify(r)) : escapeHtml(String(r));
  // round-engine sends a tri-state `verdict`; older engines only `valid`.
  const verdict = v.verdict ?? (v.valid ? 'VALID' : 'INVALID');
  const verdictHtml =
    verdict === 'VALID'
      ? '<span style="color:var(--success);font-weight:700">VALID</span>'
      : verdict === 'UNVERIFIABLE'
        ? '<span style="color:var(--warn, #fde047);font-weight:700">UNVERIFIABLE</span>'
        : '<span style="color:var(--error);font-weight:700">INVALID</span>';
  return `<div class="card">
    <h2>Verification proof</h2>
    <p class="muted" style="font-size:12px;margin:-4px 0 14px">
      Provably-fair: the server commits to <code>serverSeedHash</code> before betting opens.
      After settle the <code>serverSeed</code> is revealed; anyone can re-derive the result from
      <code>(serverSeed, clientSeed, nonce)</code> and confirm both the hash and the stored result.
    </p>
    <div class="trace-proof-grid">
      <div class="trace-proof-check">
        <div class="trace-proof-check-label">Hash commitment</div>
        <div>${yn(v.hashMatch)}</div>
        <div class="muted" style="font-size:11px">SHA-256(serverSeed) == serverSeedHash</div>
      </div>
      <div class="trace-proof-check">
        <div class="trace-proof-check-label">Result re-derivation</div>
        <div>${yn(v.resultMatch)}</div>
        <div class="muted" style="font-size:11px">re-rolled result == stored result</div>
      </div>
      <div class="trace-proof-check">
        <div class="trace-proof-check-label">Verdict</div>
        <div>${verdictHtml}</div>
        <div class="muted" style="font-size:11px">hash &amp; result both match</div>
      </div>
    </div>
    <dl class="kv trace-proof-kv">
      <dt>Server seed hash <span class="muted">(commit)</span></dt><dd><code class="trace-mono">${escapeHtml(v.serverSeedHash)}</code></dd>
      <dt>Server seed <span class="muted">(revealed)</span></dt><dd><code class="trace-mono">${escapeHtml(v.serverSeed)}</code></dd>
      <dt>Client seed</dt><dd><code class="trace-mono">${escapeHtml(v.clientSeed)}</code></dd>
      <dt>Nonce</dt><dd><code>${escapeHtml(String(v.nonce))}</code></dd>
      <dt>Stored result</dt><dd><code>${stringifyResult(v.expectedResult)}</code></dd>
      <dt>Re-derived result</dt><dd><code>${stringifyResult(v.derivedResult)}</code></dd>
      ${v.derivedPrimaryRoll !== undefined ? `<dt>Derived rolls</dt><dd><code>primary ${escapeHtml(String(v.derivedPrimaryRoll))} · special ${escapeHtml(String(v.derivedSpecialRoll))}</code></dd>` : ''}
      ${v.weightTable ? `<dt>Weight table</dt><dd><code>v${escapeHtml(String(v.weightTable.version))}</code></dd>` : ''}
    </dl>
  </div>`;
}

function renderVerdict(verdict?: 'VALID' | 'INVALID' | 'UNVERIFIABLE'): string {
  if (!verdict) return '<span class="muted">—</span>';
  const color =
    verdict === 'VALID'
      ? 'var(--success)'
      : verdict === 'UNVERIFIABLE'
        ? 'var(--warn, #fde047)'
        : 'var(--error)';
  return `<span style="color:${color};font-weight:600">${escapeHtml(verdict)}</span>`;
}

function renderBetRow(b: ChainBet): string {
  const outcomeClass = b.outcome === 'WIN' ? 'live' : b.outcome === 'VOID' ? 'warn' : '';
  // The pair, not either column alone, says what this row is. On the trace it
  // is what separates a player's UNDO (VOID + cancelled) from an admin refund
  // (VOID + confirmed) — two different rollbacks with the same outcome.
  const acceptClass = b.acceptStatus === 'confirmed' ? '' : 'warn';
  // Amounts are integer minor units of the bet's own currency; the trace names
  // it per bet. A response without it prints the integers as they are.
  const cur = b.currency ?? undefined;
  const totalPayout = b.payout
    ? Number(b.payout.baseCredits) + Number(b.payout.matchCredits) + Number(b.payout.jackpotCredits)
    : 0;
  return `
    <details class="bet-row bet-row--item">
      <summary class="bet-row__summary">
        <code class="bet-pick">${escapeHtml(betTypeLabel(b.betType))}</code>
        <code class="bet-player muted" title="${escapeHtml(b.playerId)}">${escapeHtml(b.playerId.slice(0, 8))}…</code>
        <span class="num bet-stake">${fmtMoney(b.stake, cur)}${cur ? ` <span class="muted" style="font-size:11px">${escapeHtml(cur)}</span>` : ''}</span>
        <span><span class="pill ${outcomeClass}">${escapeHtml(b.outcome)}</span></span>
        <span>${b.acceptStatus ? `<span class="pill ${acceptClass}"${acceptClass ? '' : ' style="opacity:.6"'}>${escapeHtml(b.acceptStatus)}</span>` : '<span class="muted">—</span>'}</span>
        <span class="num bet-payout ${totalPayout > 0 ? 'pos' : 'muted'}">${totalPayout > 0 ? fmtMoney(totalPayout, cur) : '—'}</span>
        <span class="num bet-moves muted">${b.movements.length}</span>
        <span class="bet-caret" aria-hidden="true">▾</span>
      </summary>
      <div class="bet-row__detail">
        ${renderPayoutBreakdown(b)}
        ${renderMovements(b.movements, cur)}
      </div>
    </details>
  `;
}

function renderPayoutBreakdown(b: ChainBet): string {
  if (!b.payout) return '';
  const p = b.payout;
  const cur = b.currency ?? undefined;
  return `<div class="bet-payout-breakdown muted">
    Payout — base ${fmtMoney(p.baseCredits, cur)} ·
    match ${fmtMoney(p.matchCredits, cur)} ·
    jackpot ${fmtMoney(p.jackpotCredits, cur)}${cur ? ` ${escapeHtml(cur)}` : ''}
  </div>`;
}

function renderMovements(movements: ChainMovement[], cur: string | undefined): string {
  if (movements.length === 0) return '<p class="muted">No wallet movements recorded.</p>';
  return `<div class="trace-mv-list">${movements
    .map((m) => {
      const integrity = m.sealed
        ? `<span class="integrity integrity-sealed" title="Hash sealed into the append-only audit chain — tamper-evident.">${icon('lock', { size: 13 })}<span>sealed</span></span>`
        : `<span class="integrity integrity-pending" title="Written, but the audit-chain sealing worker hasn't stamped it yet (normal for a fresh movement).">${icon('unlock', { size: 13 })}<span>pending seal</span></span>`;
      return `<details class="trace-mv" id="wtx-${escapeHtml(m.walletTxId)}">
        <summary class="trace-mv-summary">
          <span class="tag" style="background:${kindColor(m.kind)};color:#fff;padding:2px 8px;border-radius:6px;font-size:11px;font-weight:600">${escapeHtml(m.kind)}</span>
          <span class="trace-mv-credits" style="font-variant-numeric:tabular-nums">${fmtMoney(m.creditAmount, cur)}</span>
          <code class="trace-mv-status">${escapeHtml(m.status)}</code>
          <span class="muted trace-mv-when">${escapeHtml(fmtDateTime(m.createdAt))}</span>
          <span class="trace-mv-integrity">${integrity}</span>
          <span class="trace-mv-caret">▾</span>
        </summary>
        <dl class="kv trace-mv-ids">
          <dt>Wallet tx id</dt><dd><code class="trace-mono">${escapeHtml(m.walletTxId)}</code></dd>
          <dt>Request id <span class="muted">(idempotency key)</span></dt><dd><code class="trace-mono">${escapeHtml(m.requestId)}</code></dd>
          <dt>Ref tx id <span class="muted">(integrator)</span></dt><dd>${m.refTxId ? `<code class="trace-mono">${escapeHtml(m.refTxId)}</code>` : '<span class="muted">—</span>'}</dd>
          <dt>Parent tx id</dt><dd>${m.parentTxId ? `<code class="trace-mono">${escapeHtml(m.parentTxId)}</code>` : '<span class="muted">—</span>'}</dd>
          <dt>Audit chain</dt><dd>${m.sealed ? 'sealed (hash-chained, tamper-evident)' : '<span style="color:var(--warn)">unsealed (pending seal)</span>'}</dd>
        </dl>
      </details>`;
    })
    .join('')}</div>`;
}

function kindColor(kind: ChainMovement['kind']): string {
  switch (kind) {
    case 'debit':
      return 'var(--error)';
    case 'win':
      return 'var(--success)';
    case 'jackpot':
      return 'var(--accent-2)';
    case 'rollback':
      return 'var(--warn)';
    default:
      return 'var(--muted, #888)';
  }
}

function stateClass(state: string): string {
  return state === 'betting_open' ? 'live' : state === 'finished' ? '' : 'warn';
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) {
    if (err.status === 404 || err.code === 'TRACE_NOT_FOUND') {
      return 'No transaction found for that id.';
    }
    return `${err.code}: ${err.message}`;
  }
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string | null | undefined): string {
  return (s ?? '').toString().replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
